import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Phone, Trash2, RefreshCw, CheckCircle, XCircle, AlertCircle, ArrowLeft, LogOut, Key, Settings, RotateCcw } from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient, API_BASE_URL } from '../../services/api';
import UpdateApiKeyModal from '../../components/UpdateApiKeyModal';
import ConfirmDialog, { useConfirmDialog } from '../../components/ui/ConfirmDialog';

interface Tenant {
  id: string;
  name: string;
  status: string;
}

interface WabaAccount {
  id: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  status?: string;
  qualityRating?: string;
  messagingLimitTier?: string;
  maxDailyConversations?: number;
  accountStatus?: string;
  lastQualityCheck?: string;
  createdAt: string;
  tenantId: string;
  dailyDispatchLimit?: number;
}

interface DispatchLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  wabaId: string;
  wabaPhone: string;
  currentLimit: number;
  onSuccess: () => void;
}

function DispatchLimitModal({ isOpen, onClose, wabaId, wabaPhone, currentLimit, onSuccess }: DispatchLimitModalProps) {
  const { accessToken } = useAuth();
  const [limit, setLimit] = useState(currentLimit.toString());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { confirm: showConfirm, dialogProps } = useConfirmDialog();

  useEffect(() => {
    if (isOpen) {
      setLimit(currentLimit.toString());
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen, currentLimit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    try {
      const limitValue = parseInt(limit, 10);
      if (isNaN(limitValue) || limitValue < 0) {
        throw new Error('O limite deve ser um número inteiro >= 0');
      }

      const response = await apiClient.updateWabaDispatchLimit(wabaId, limitValue, accessToken!);

      if (response.success) {
        setSuccessMessage(limitValue === 0 ? 'Limite removido - conta agora é ilimitada' : `Limite definido para ${limitValue.toLocaleString()} mensagens/dia`);
        onSuccess();
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(response.message || 'Erro ao atualizar limite');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar limite');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    showConfirm({
      title: 'Zerar contador de uso',
      message: 'Tem certeza que deseja zerar o contador de uso? Esta ação não pode ser desfeita.',
      variant: 'danger',
      confirmText: 'Zerar',
      onConfirm: async () => {
        setError(null);
        setSuccessMessage(null);
        setIsResetting(true);

        try {
          const response = await apiClient.resetWabaDispatchUsage(wabaId, accessToken!);

          if (response.success) {
            setSuccessMessage(`Uso zerado! ${response.data?.deletedCount || 0} registros removidos.`);
            onSuccess();
          } else {
            throw new Error(response.message || 'Erro ao zerar uso');
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Erro ao zerar uso');
        } finally {
          setIsResetting(false);
        }
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-1 rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-text-1 mb-2">
            Limite de Disparo da Conta
          </h2>
          <p className="text-sm text-text-2 mb-4">
            {wabaPhone}
          </p>

          <div className="bg-info/10 border border-info/30 rounded-md p-3 mb-4">
            <p className="text-sm text-info">
              <strong>0 = Ilimitado:</strong> Por padrão, contas WABA não têm limite de disparo configurado.
              Defina um valor maior que zero para limitar os disparos diários (janela de 24h rolante).
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-2 mb-1">
                Limite Diário de Disparos
              </label>
              <input
                type="number"
                min="0"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                placeholder="0 = ilimitado"
                className="w-full px-3 py-2 border border-border-1 rounded-md focus:ring-2 focus:ring-accent-1 bg-bg-1 text-text-1"
                required
              />
              <p className="text-xs text-text-3 mt-1">
                Este limite é compartilhado por todos os usuários que utilizam esta conta WABA
              </p>
            </div>

            {error && (
              <div className="p-3 bg-danger/10 border border-danger/30 rounded-md">
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}

            {successMessage && (
              <div className="p-3 bg-success/10 border border-success/30 rounded-md">
                <p className="text-sm text-success">{successMessage}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-border-1 text-text-2 rounded-md hover:bg-bg-3/50 transition-colors"
                disabled={isSubmitting || isResetting}
              >
                Fechar
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isResetting}
                className="flex-1 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </button>
            </div>

            <div className="border-t border-border-1 pt-4 mt-4">
              <button
                type="button"
                onClick={handleReset}
                disabled={isSubmitting || isResetting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-warning/50 text-warning rounded-md hover:bg-warning/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw size={16} className={isResetting ? 'animate-spin' : ''} />
                {isResetting ? 'Zerando...' : 'Zerar Uso (24h)'}
              </button>
              <p className="text-xs text-text-3 text-center mt-2">
                Remove todos os registros de uso das últimas 24h desta conta
              </p>
            </div>
          </form>
        </div>
      </div>
      <ConfirmDialog {...dialogProps} />
    </div>
  );
}

interface CreateWabaModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  onSuccess: () => void;
}

function CreateWabaModal({ isOpen, onClose, tenantId, onSuccess }: CreateWabaModalProps) {
  const { accessToken } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/waba/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          apiKey,
          displayPhoneNumber,
          tenantId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao conectar conta WABA');
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao conectar conta WABA');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setApiKey('');
    setDisplayPhoneNumber('');
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-1 rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-text-1 mb-4">
            Conectar Nova Conta WABA
          </h2>

          <div className="bg-info/10 border border-info/30 rounded-md p-3 mb-4">
            <p className="text-sm text-info">
              <strong>Cadastro Automático:</strong> Informe apenas a API Key e o número de telefone.
              O sistema buscará automaticamente todas as informações necessárias do Dialog360.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-2 mb-1">
                API Key (Dialog360) *
              </label>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="D360-API-KEY-XXXXXXXXXXXXXX"
                className="w-full px-3 py-2 border border-border-1 rounded-md focus:ring-2 focus:ring-accent-1 bg-bg-1 text-text-1"
                required
              />
              <p className="text-xs text-text-3 mt-1">
                Chave de API do Dialog360 para este número
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-2 mb-1">
                Número de Telefone *
              </label>
              <input
                type="tel"
                value={displayPhoneNumber}
                onChange={(e) => setDisplayPhoneNumber(e.target.value)}
                placeholder="+5511999999999"
                className="w-full px-3 py-2 border border-border-1 rounded-md focus:ring-2 focus:ring-accent-1 bg-bg-1 text-text-1"
                required
              />
              <p className="text-xs text-text-3 mt-1">
                Formato internacional: +DDI + DDD + Número (ex: +5511999999999)
              </p>
            </div>

            {error && (
              <div className="p-3 bg-danger/10 border border-danger/30 rounded-md">
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  resetForm();
                }}
                className="flex-1 px-4 py-2 border border-border-1 text-text-2 rounded-md hover:bg-bg-3/50 transition-colors"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Conectando...' : 'Conectar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function WabaManagement() {
  const { accessToken, logout } = useAuth();
  const navigate = useNavigate();
  const { confirm: showConfirm, dialogProps } = useConfirmDialog();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [wabaAccounts, setWabaAccounts] = useState<WabaAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updateApiKeyModal, setUpdateApiKeyModal] = useState<{
    isOpen: boolean;
    wabaId: string;
    wabaPhone: string;
  }>({
    isOpen: false,
    wabaId: '',
    wabaPhone: '',
  });
  const [dispatchLimitModal, setDispatchLimitModal] = useState<{
    isOpen: boolean;
    wabaId: string;
    wabaPhone: string;
    currentLimit: number;
  }>({
    isOpen: false,
    wabaId: '',
    wabaPhone: '',
    currentLimit: 0,
  });

  // Carregar tenants ao montar o componente
  useEffect(() => {
    loadTenants();
  }, []);

  // Carregar contas WABA quando o tenant for selecionado
  useEffect(() => {
    if (selectedTenantId) {
      loadWabaAccounts();
    } else {
      setWabaAccounts([]);
    }
  }, [selectedTenantId]);

  const loadTenants = async () => {
    if (!accessToken) {
      return;
    }

    try {
      const response = await apiClient.getAllTenants(accessToken);

      if (response.success && response.data) {
        setTenants(response.data.data || []);
      }
    } catch {
      setError('Erro ao carregar tenants');
      setTenants([]);
    }
  };

  const loadWabaAccounts = async () => {
    if (!accessToken || !selectedTenantId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Usar o parâmetro tenantId da API para filtrar as contas
      const response = await apiClient.getWabaAccounts(accessToken, selectedTenantId);

      if (response.success && response.data) {
        setWabaAccounts(response.data.wabaAccounts);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar contas WABA');
      setWabaAccounts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncAll = async () => {
    if (!selectedTenantId || !accessToken) return;

    setIsSyncing(true);
    setError(null);

    try {
      // Sync-all sincroniza TODAS as contas WABA do sistema com Dialog360
      // Isso é recomendado para super admins pois mantém todos os dados atualizados
      const response = await fetch(`${API_BASE_URL}/api/waba/sync-all`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erro ao sincronizar contas WABA');
      }

      // Recarregar contas após sincronização
      await loadWabaAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao sincronizar contas WABA');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDeleteWaba = (id: string) => {
    if (!accessToken) return;

    showConfirm({
      title: 'Desconectar conta WABA',
      message: 'Tem certeza que deseja desconectar esta conta WABA?',
      variant: 'danger',
      confirmText: 'Desconectar',
      onConfirm: async () => {
        try {
          const response = await apiClient.deleteWabaAccount(id, accessToken);

          if (response.success) {
            await loadWabaAccounts();
          } else {
            throw new Error('Erro ao desconectar conta WABA');
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Erro ao desconectar conta WABA');
        }
      },
    });
  };

  const getQualityBadge = (rating?: string) => {
    if (!rating) return null;

    const colors = {
      GREEN: 'bg-success/10 text-success',
      YELLOW: 'bg-warning/10 text-warning',
      RED: 'bg-danger/10 text-danger',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${colors[rating as keyof typeof colors] || 'bg-bg-3 text-text-1'}`}>
        {rating}
      </span>
    );
  };

  const getStatusIcon = (accountStatus?: string, status?: string) => {
    const displayStatus = accountStatus || status;

    switch (displayStatus) {
      case 'CONNECTED':
      case 'active':
        return <CheckCircle className="text-success" size={16} />;
      case 'BANNED':
      case 'RESTRICTED':
      case 'inactive':
        return <XCircle className="text-danger" size={16} />;
      case 'FLAGGED':
      case 'pending':
        return <AlertCircle className="text-warning" size={16} />;
      default:
        return <AlertCircle className="text-text-3" size={16} />;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center text-text-2 hover:text-text-1"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Dashboard
          </button>
          <button
            onClick={() => {
              logout();
              navigate('/login');
            }}
            className="flex items-center gap-2 px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>

        <h1 className="text-2xl font-bold text-text-1 mb-2">
          Gerenciar Contas WABA
        </h1>
        <p className="text-text-2">
          Gerencie as contas WhatsApp Business API das empresas
        </p>
      </div>

      {/* Seletor de Tenant */}
      <div className="bg-bg-2 rounded-lg shadow p-6 mb-6">
        <label className="block text-sm font-medium text-text-2 mb-2">
          Selecione uma Empresa
        </label>
        <select
          value={selectedTenantId}
          onChange={(e) => setSelectedTenantId(e.target.value)}
          className="w-full px-4 py-2 border border-border-1 rounded-md focus:ring-2 focus:ring-accent-1 bg-bg-1 text-text-1"
        >
          <option value="">-- Selecione uma empresa --</option>
          {(tenants || []).map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name} ({tenant.status})
            </option>
          ))}
        </select>
      </div>

      {/* Lista de Contas WABA */}
      {selectedTenantId && (
        <>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-text-1">
              Contas WABA
            </h2>
            <div className="flex gap-2">
              <button
                onClick={handleSyncAll}
                disabled={isSyncing || wabaAccounts.length === 0}
                className="flex items-center gap-2 px-4 py-2 border border-border-1 text-text-2 rounded-md hover:bg-bg-3/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw size={16} className={isSyncing ? 'animate-spin' : ''} />
                Sincronizar Tudo
              </button>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-md transition-colors"
              >
                <Plus size={16} />
                Conectar Conta WABA
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-danger/10 border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {isLoading ? (
            <LoadingSpinner size="lg" text="Carregando contas WABA..." />
          ) : wabaAccounts.length === 0 ? (
            <div className="bg-bg-2 rounded-lg shadow p-8 text-center">
              <Phone size={48} className="mx-auto text-text-3 mb-4" />
              <h3 className="text-lg font-medium text-text-1 mb-2">
                Nenhuma conta WABA conectada
              </h3>
              <p className="text-text-2 mb-4">
                Conecte uma conta WhatsApp Business API para começar
              </p>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-md transition-colors"
              >
                <Plus size={16} />
                Conectar Conta WABA
              </button>
            </div>
          ) : (
            <div className="bg-bg-2 rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-border-1">
                <thead className="bg-bg-3">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                      Número
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                      Phone Number ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                      Qualidade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                      Limite de Mensagens
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                      Limite Disparo/Dia
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                      Conectado em
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-bg-2 divide-y divide-border-1">
                  {wabaAccounts.map((account) => (
                    <tr key={account.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(account.accountStatus, account.status)}
                          <span className="text-sm text-text-1">
                            {account.accountStatus || account.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-text-1">
                          {account.displayPhoneNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-text-3">
                          {account.phoneNumberId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getQualityBadge(account.qualityRating)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {account.messagingLimitTier && account.maxDailyConversations ? (
                          <div className="text-sm">
                            <div className="font-medium text-text-1">
                              {account.messagingLimitTier}
                            </div>
                            <div className="text-text-3">
                              {account.maxDailyConversations.toLocaleString()} conv/dia
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-text-3">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {account.dailyDispatchLimit !== undefined && account.dailyDispatchLimit > 0 ? (
                          <span className="px-2 py-1 text-xs font-medium rounded bg-accent-1/10 text-accent-1">
                            {account.dailyDispatchLimit.toLocaleString()}/dia
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded bg-bg-3 text-text-3">
                            Ilimitado
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-text-3">
                          {new Date(account.createdAt).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setDispatchLimitModal({
                                isOpen: true,
                                wabaId: account.id,
                                wabaPhone: account.displayPhoneNumber,
                                currentLimit: account.dailyDispatchLimit || 0,
                              });
                            }}
                            className="text-accent-1 hover:text-accent-1/80 transition-colors"
                            title="Configurar limite de disparo"
                          >
                            <Settings size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setUpdateApiKeyModal({
                                isOpen: true,
                                wabaId: account.id,
                                wabaPhone: account.displayPhoneNumber,
                              });
                            }}
                            className="text-accent-1 hover:text-accent-2 transition-colors"
                            title="Atualizar API Key"
                          >
                            <Key size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteWaba(account.id)}
                            className="text-danger hover:text-danger/80"
                            title="Desconectar conta WABA"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <CreateWabaModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        tenantId={selectedTenantId}
        onSuccess={loadWabaAccounts}
      />

      <UpdateApiKeyModal
        isOpen={updateApiKeyModal.isOpen}
        onClose={() =>
          setUpdateApiKeyModal({ isOpen: false, wabaId: '', wabaPhone: '' })
        }
        wabaId={updateApiKeyModal.wabaId}
        wabaPhone={updateApiKeyModal.wabaPhone}
        accessToken={accessToken}
        onSuccess={loadWabaAccounts}
      />

      <DispatchLimitModal
        isOpen={dispatchLimitModal.isOpen}
        onClose={() =>
          setDispatchLimitModal({ isOpen: false, wabaId: '', wabaPhone: '', currentLimit: 0 })
        }
        wabaId={dispatchLimitModal.wabaId}
        wabaPhone={dispatchLimitModal.wabaPhone}
        currentLimit={dispatchLimitModal.currentLimit}
        onSuccess={loadWabaAccounts}
      />

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}
